'use client';

import React, { useEffect, useRef, useState, KeyboardEvent } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import CloseIcon from '../icons/close2';
import HelpIcon from '../icons/help';

type Content = {
  ContentToolTip?: React.ReactElement;
};

type Props = {
  className?: string;
  Content: Content;
  ariaLabel?: string;
};

const ToolTips: React.FC<Props> = ({ Content, className, ariaLabel = 'Help information' }) => {
  const [toolTipVisible, setToolTipVisible] = useState(false);
  const [isKeyboard, setIsKeyboard] = useState(false);
  const [open, setOpen] = useState(false);

  const tooltipContentRef = useRef<HTMLDivElement>(null);
  const contentSectionRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleClose = (e: React.MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    setToolTipVisible(false);
    triggerRef.current?.focus();
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
    setToolTipVisible(true);
    if (!open) {
      setTimeout(() => {
        if (contentSectionRef.current) {
          contentSectionRef.current.focus();
        }
      }, 0);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose(e);
    } else if (e.key === 'Tab') {
      setIsKeyboard(true);
      e.preventDefault();
      const focusableElements = tooltipContentRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements?.length - 1];
      const currentElement = document.activeElement;

      const elementsArray = Array.from(focusableElements);
      const currentIndex = elementsArray.indexOf(currentElement as HTMLElement);

      if (e.shiftKey) {
        if (currentIndex <= 0) {
          lastElement.focus();
        } else {
          elementsArray[currentIndex - 1].focus();
        }
      } else {
        if (currentIndex === elementsArray?.length - 1) {
          firstElement.focus();
        } else {
          elementsArray[currentIndex + 1].focus();
        }
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      tooltipContentRef?.current &&
      !tooltipContentRef?.current?.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current?.contains(event.target as Node)
    ) {
      setOpen(false);
      setToolTipVisible(false);
    }
  };

  useEffect(() => {
    const handleMouseDown = () => {
      setIsKeyboard(false);
    };

    if (open) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown as unknown as EventListener);
    } else {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown as unknown as EventListener);
    }

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown as unknown as EventListener);
    };
  }, [open]);

  let startY = 0;
  let scrollStart = 0;

  useEffect(() => {
    const handleScroll = (event: any) => {
      const tooltipScrollbar = document?.querySelector('.tooltip-scrollbar');
      if (tooltipScrollbar && open) {
        if (
          (event instanceof TouchEvent && event.type === 'touchstart') ||
          event.type === 'touchmove' ||
          event.type === 'touchend'
        ) {
          const touchEvent = event as TouchEvent;
          if (touchEvent.type === 'touchstart') {
            startY = touchEvent.touches[0].pageY;
            scrollStart = tooltipScrollbar.scrollTop;
          }
          if (touchEvent.type === 'touchmove') {
            const touchMoveY = touchEvent.touches[0].pageY;
            const scrollDifference = touchMoveY - startY;
            tooltipScrollbar.scrollTop = scrollStart - scrollDifference;
          }
        } else if (event.type === 'wheel') {
          tooltipScrollbar.scrollTop += event.deltaY;
          if (tooltipScrollbar.scrollHeight > tooltipScrollbar.clientHeight) {
            event.preventDefault();
          }
        }
      }
    };

    if (toolTipVisible) {
      document.addEventListener('wheel', handleScroll, { passive: false });
      document.addEventListener('touchstart', handleScroll, { passive: false });
      document.addEventListener('touchmove', handleScroll, { passive: false });
      document.addEventListener('touchend', handleScroll, { passive: false });
    }

    return () => {
      document.removeEventListener('wheel', handleScroll);
      document.addEventListener('touchstart', handleScroll);
      document.addEventListener('touchmove', handleScroll);
      document.addEventListener('touchend', handleScroll);
    };
  }, [toolTipVisible, open]);

  return (
    <section>
      <Tooltip.Provider>
        <Tooltip.Root open={open}>
          <Tooltip.Trigger
            ref={triggerRef}
            onClick={handleTriggerClick}
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-controls="tooltip-content"
            type="button"
            className={`${isKeyboard ? 'focus:outline-none focus:ring-1 focus:ring-black' : ''} ${className}`}
            id="tooltip-content"
          >
            <HelpIcon />
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              ref={tooltipContentRef}
              id="tooltip-content"
              role="dialog"
              className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade 
                data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade 
                data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade 
                relative
                left-1 z-[9999] mr-2 select-none rounded-[4px] border border-silverGray 
                bg-white px-3 pb-3 pt-2 text-sm leading-4 text-black
                drop-shadow-lg will-change-[transform,opacity]"
              sideOffset={5}
              aria-labelledby="tooltip"
            >
              <section className="pb-4">
                <button
                  ref={closeButtonRef}
                  onClick={handleClose}
                  aria-label="Close tooltip"
                  className={`absolute right-3 top-2 rounded p-1
                     ${isKeyboard ? 'focus:outline-none focus:ring-1 focus:ring-black' : ''}`}
                  tabIndex={0}
                >
                  <CloseIcon className="cursor-pointer fill-silverGray" />
                </button>
              </section>

              <section
                ref={contentSectionRef}
                className={`tooltip-scrollbar custom-scrollbar m-2 max-h-[246px] 
                  max-w-[314px] overflow-auto ${isKeyboard ? 'focus:outline-none focus:ring-1 focus:ring-black' : ''}`}
                role="region"
                aria-label="Tooltip content"
                tabIndex={0}
              >
                {Content.ContentToolTip}
              </section>

              <Tooltip.Arrow className="fill-white drop-shadow-lg" height={20} width={20} />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </section>
  );
};

export default ToolTips;
